import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

interface EditNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issueId: string;
  activityId: string;
  currentNote: string;
  originalAuthor: string;
}

export function EditNoteDialog({
  open,
  onOpenChange,
  issueId,
  activityId,
  currentNote,
  originalAuthor,
}: EditNoteDialogProps) {
  const [noteText, setNoteText] = useState(currentNote);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Reset note text when dialog opens with new values
  useEffect(() => {
    if (open) {
      setNoteText(currentNote);
    }
  }, [open, currentNote]);

  const updateNoteMutation = useMutation({
    mutationFn: async (data: { note: string }) => {
      const response = await apiRequest("PATCH", `/api/crm/issues/${issueId}/activity/${activityId}`, {
        notes: data.note,
      });

      return response.json();
    },
    onSuccess: () => {
      // Invalidate activity log to refresh
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/issues", issueId, "activity"]
      });

      // Show success toast
      toast({
        title: "Note updated",
        description: "Your note has been updated successfully.",
      });

      // Close dialog
      onOpenChange(false);
    },
    onError: (error: Error) => {
      // Show error toast
      toast({
        title: "Failed to update note",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!noteText.trim()) {
      toast({
        title: "Note is required",
        description: "Please enter a note before submitting.",
        variant: "destructive",
      });
      return;
    }

    // Submit mutation
    updateNoteMutation.mutate({
      note: noteText.trim(),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Note</DialogTitle>
            <DialogDescription>
              Update the note content. Original author: {originalAuthor}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Note Text Field */}
            <div className="grid gap-2">
              <Label htmlFor="note">Note</Label>
              <Textarea
                id="note"
                placeholder="Enter note text..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={6}
                className="resize-none"
                disabled={updateNoteMutation.isPending}
              />
            </div>

            {/* Error Alert */}
            {updateNoteMutation.isError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {updateNoteMutation.error?.message || "Failed to update note. Please try again."}
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateNoteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!noteText.trim() || updateNoteMutation.isPending}
            >
              {updateNoteMutation.isPending ? "Updating..." : "Update Note"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
